import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { Environment } from '@/react/portainer/environments/types';
import {
  isEdgeEnvironment,
  isEdgeAsync,
} from '@/react/portainer/environments/utils';
import { DockerSnapshotPanel } from '@/react/edge/components/SnapshotPanel';
import { environmentStore } from '@/react/hooks/current-environment-store';

import { confirm } from '@@/modals/confirm';
import { PageHeader } from '@@/PageHeader';
import { ModalType } from '@@/modals';
import { buildConfirmButton } from '@@/modals/utils';

import { useEnvironment } from '../environments/queries';
import { useWhiteLabel } from '../feature-flags/useWhiteLabel';

import { EnvironmentList } from './EnvironmentList';
import { EdgeLoadingSpinner } from './EdgeLoadingSpinner';
import { MotdPanel } from './MotdPanel';
import { LicenseNodePanel } from './LicenseNodePanel';
import { BackupFailedPanel } from './BackupFailedPanel';
import {
  EnvironmentHeader,
  HeaderFilter,
} from './EnvironmentHeader/EnvironmentHeader';
import { useHomePageFilter } from './EnvironmentList/HomepageFilter';

export function HomeView() {
  const { environmentId: currentEnvironmentId, clear: clearStore } =
    useStore(environmentStore);
  const environmentQuery = useEnvironment(currentEnvironmentId);

  const { params } = useCurrentStateAndParams();
  const [connectingToEdgeEndpoint, setConnectingToEdgeEndpoint] = useState(
    !!params.redirect
  );
  const [headerFilter, setHeaderFilter] = useHomePageFilter<HeaderFilter>(
    'headerFilter',
    'all'
  );

  const router = useRouter();
  const { data: whitelabel } = useWhiteLabel();

  useEffect(() => {
    async function redirect() {
      const options = {
        title: `Failed connecting to ${params.environmentName}`,
        message: `There was an issue connecting to edge agent via tunnel. Click 'Retry' below to retry now, or wait 10 seconds to automatically retry.`,
        confirmButton: buildConfirmButton('Retry', 'primary', 10),
        modalType: ModalType.Destructive,
      };

      if (await confirm(options)) {
        setConnectingToEdgeEndpoint(true);
        router.stateService.go(params.route, {
          endpointId: params.environmentId,
        });
      } else {
        clearStore();
        router.stateService.go(
          'portainer.home',
          {},
          { reload: true, inherit: false }
        );
      }
    }

    if (params.redirect) {
      redirect();
    }
  }, [params, setConnectingToEdgeEndpoint, router, clearStore]);

  const environment = environmentQuery.data;
  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader title="Home" breadcrumbs={[{ label: 'Environments' }]} reload>
        {environment && <DockerSnapshotPanel environment={environment} />}
      </PageHeader>

      {process.env.PORTAINER_EDITION !== 'CE' && <LicenseNodePanel />}

      {!whitelabel && <MotdPanel />}

      {process.env.PORTAINER_EDITION !== 'CE' && <BackupFailedPanel />}

      {connectingToEdgeEndpoint ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <EdgeLoadingSpinner />
        </div>
      ) : (
        <div className="mx-5 flex flex-col gap-6">
          <EnvironmentHeader
            activeFilter={headerFilter}
            onFilterChange={setHeaderFilter}
          />
          <EnvironmentList
            onClickBrowse={handleBrowseClick}
            headerFilter={headerFilter}
            onHeaderFilterChange={setHeaderFilter}
          />
        </div>
      )}
    </div>
  );

  function handleBrowseClick(environment: Environment) {
    if (isEdgeEnvironment(environment.Type) && !isEdgeAsync(environment)) {
      setConnectingToEdgeEndpoint(true);
    }
  }
}
